import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';
import type { View } from 'react-native';

import type { UseWebDialogBehaviorOptions } from './types';

export const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const openDialogs: HTMLElement[] = [];

const exposeTopmostDialog = () => {
  openDialogs.forEach((dialog, index) => {
    if (index < openDialogs.length - 1) {
      dialog.setAttribute('inert', '');
      dialog.setAttribute('aria-hidden', 'true');
    } else {
      dialog.removeAttribute('inert');
      dialog.removeAttribute('aria-hidden');
    }
  });
};

export const useWebDialogBehavior = (
  ref: RefObject<View | null>,
  {
    active,
    onDismiss,
    dismissable = true,
    initialFocus = 'first',
  }: UseWebDialogBehaviorOptions,
): void => {
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;
  const dismissableRef = useRef(dismissable);
  dismissableRef.current = dismissable;

  useEffect(() => {
    if (!active) return;
    const node = ref.current as unknown as HTMLElement | null;
    if (!node || typeof document === 'undefined') return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    openDialogs.push(node);
    exposeTopmostDialog();
    const focusables = () =>
      Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => el.offsetParent !== null,
      );

    // preventScroll everywhere: the sheet and the control that opened it are
    // still animating when focus moves, and a scroll-into-view here shifts
    // the screen behind the sheet (SQR-13995).
    (initialFocus === 'container' ? node : (focusables()[0] ?? node)).focus?.({
      preventScroll: true,
    });

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Stop here so an outer dialog (or the widget's Escape handler) does
        // not also act on the same key.
        event.stopPropagation();
        if (dismissableRef.current) onDismissRef.current?.();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) {
        event.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (
        event.shiftKey &&
        (document.activeElement === first || document.activeElement === node)
      ) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    };

    const onScroll = (event: Event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement) || target === node) return;
      if (!target.contains(node) || target.scrollTop === 0) return;
      if (getComputedStyle(target).overflowY !== 'hidden') return;
      target.scrollTop = 0;
    };

    node.addEventListener('keydown', onKeyDown);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      node.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('scroll', onScroll, true);
      openDialogs.splice(openDialogs.indexOf(node), 1);
      node.removeAttribute('inert');
      node.removeAttribute('aria-hidden');
      exposeTopmostDialog();
      previouslyFocused?.focus?.({ preventScroll: true });
    };
  }, [ref, active, initialFocus]);
};
